import type { RouteComponent, RouteRecordRaw } from 'vue-router';
import type { Locale } from '@/i18n';

type Loader = () => Promise<RouteComponent | { default: RouteComponent }>;

export interface LocalizedRouteDef {
  key: string;
  pt: string;
  en: string;
  component: Loader;
  prerender?: boolean;
  props?: Record<string, unknown>;
}

export const CONTENT_KEYS = ['what-is', 'how-to-play', 'fibonacci', 'tshirt'] as const;
export type ContentKey = (typeof CONTENT_KEYS)[number];
export const LEGAL_KEYS = ['privacy', 'terms'] as const;
export type LegalKey = (typeof LEGAL_KEYS)[number];

const ContentPage: Loader = () => import('@/pages/ContentPage.vue');

export const LOCALIZED_ROUTES: LocalizedRouteDef[] = [
  { key: 'landing', pt: '/', en: '/en', component: () => import('@/pages/LandingPage.vue'), prerender: true },
  { key: 'new-room', pt: '/nova-sala', en: '/en/new-room', component: () => import('@/pages/NewRoomPage.vue') },
  { key: 'what-is', pt: '/o-que-e-planning-poker', en: '/en/what-is-planning-poker', component: ContentPage, prerender: true, props: { contentKey: 'what-is' } },
  { key: 'how-to-play', pt: '/como-jogar-planning-poker', en: '/en/how-to-play-planning-poker', component: ContentPage, prerender: true, props: { contentKey: 'how-to-play' } },
  { key: 'fibonacci', pt: '/sequencia-fibonacci-estimativa', en: '/en/fibonacci-estimation', component: ContentPage, prerender: true, props: { contentKey: 'fibonacci' } },
  { key: 'tshirt', pt: '/tamanho-de-camiseta-estimativa', en: '/en/t-shirt-size-estimation', component: ContentPage, prerender: true, props: { contentKey: 'tshirt' } },
  { key: 'privacy', pt: '/privacidade', en: '/en/privacy', component: ContentPage, prerender: true, props: { contentKey: 'privacy' } },
  { key: 'terms', pt: '/termos', en: '/en/terms', component: ContentPage, prerender: true, props: { contentKey: 'terms' } },
  { key: 'sign-in', pt: '/conta/entrar', en: '/en/account/sign-in', component: () => import('@/pages/account/SignInPage.vue') },
  { key: 'my-rooms', pt: '/minhas-salas', en: '/en/my-rooms', component: () => import('@/pages/account/MyRoomsPage.vue') },
  { key: 'account', pt: '/conta', en: '/en/account', component: () => import('@/pages/account/AccountPage.vue') },
  { key: 'room-not-found', pt: '/sala-nao-encontrada', en: '/en/room-not-found', component: () => import('@/pages/NotFoundPage.vue'), props: { kind: 'room' } },
];

export function localizedName(key: string, locale: Locale): string {
  return locale === 'en' ? `${key}-en` : key;
}

export function pathFor(key: string, locale: Locale): string | null {
  const def = LOCALIZED_ROUTES.find((route) => route.key === key);
  if (!def) return null;
  return locale === 'en' ? def.en : def.pt;
}

export const PRERENDER_PATHS = LOCALIZED_ROUTES.filter((route) => route.prerender).flatMap((route) => [route.pt, route.en]);

export function buildRoutes(): RouteRecordRaw[] {
  const localized = LOCALIZED_ROUTES.flatMap((def): RouteRecordRaw[] => [
    { path: def.pt, name: def.key, component: def.component, props: def.props ?? false, meta: { locale: 'pt-BR', key: def.key } },
    { path: def.en, name: `${def.key}-en`, component: def.component, props: def.props ?? false, meta: { locale: 'en', key: def.key } },
  ]);
  return [
    ...localized,
    {
      path: '/r/:code',
      component: () => import('@/pages/room/RoomShell.vue'),
      props: true,
      children: [
        { path: '', name: 'room', component: () => import('@/pages/room/RoomPage.vue'), meta: { key: 'room' } },
        { path: 'configurar', name: 'room-settings', component: () => import('@/pages/room/RoomSettingsPage.vue'), meta: { key: 'room-settings' } },
        { path: 'historias/importar', name: 'room-import', component: () => import('@/pages/room/ImportStoriesPage.vue'), meta: { key: 'room-import' } },
        { path: 'resultado', name: 'room-result', component: () => import('@/pages/room/ResultPage.vue'), meta: { key: 'room-result' } },
      ],
    },
    { path: '/conta/link', name: 'magic-link', component: () => import('@/pages/account/MagicLinkPage.vue'), meta: { key: 'magic-link' } },
    { path: '/minhas-salas/:code/historico', name: 'room-history', component: () => import('@/pages/account/RoomHistoryPage.vue'), props: true, meta: { key: 'room-history' } },
    { path: '/minhas-salas/:code/editar', name: 'room-edit', component: () => import('@/pages/account/RoomEditPage.vue'), props: true, meta: { key: 'room-edit' } },
    { path: '/interno/metricas', name: 'internal-metrics', component: () => import('@/pages/account/InternalMetricsPage.vue'), meta: { key: 'internal-metrics' } },
    { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/pages/NotFoundPage.vue'), props: { kind: 'page' }, meta: { key: 'not-found' } },
  ];
}

declare module 'vue-router' {
  interface RouteMeta {
    locale?: Locale;
    key?: string;
  }
}
